import React, { useEffect, useState } from "react";
import {
    Button,
    Grid,
    Paper,
    Stack,
    Typography,
    Box,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    IconButton,
    Tooltip,
    TextField,
    Card,
} from "@mui/material";
import {
    FilterList as FilterListIcon,
    Clear as ClearIcon,
    Refresh as RefreshIcon,
    GetApp as ExportIcon,
    Assignment as AssignmentIcon,
} from "@mui/icons-material";

import WarrantyTableComponent from "../../../components/admin/warranty-manager/WarrantyTable";
import WarrantyDetailsDialog from "../../../components/admin/warranty-manager/WarrantyDetailsDialog";

import { useGetAdminWarrantyRegistrationsQuery } from "../../../store/warranty/warrantyApis";
import { defaultPaginate } from "../../../helpers/constants";
import { useGetProductsDropdownQuery } from "../../../store/masters/mastersApis";
import { SearchRecords } from "../../../components/Common/SearchRecords";

const WarrantyContainer = (props) => {
    const { showDrawer, formType, initialValues, pagination, filters, selectedWarranty, showWarrantyDetails } = props;

    const [showFilters, setShowFilters] = useState(true);
    const [selectedWarrantyForDetails, setSelectedWarrantyForDetails] = useState(null);

    // Build query parameters
    const queryParams = {
        page: pagination.pageNo + 1,
        limit: pagination.limit,
        sort_by: pagination.sortField,
        sort_order: pagination.sortBy,
        search: pagination.keywords,
        // Only include non-null filters
        ...(filters.status && filters.status !== "null" && { status: filters.status }),
        ...(filters.product_id && filters.product_id !== "null" && { product_id: filters.product_id }),
        ...(filters.start_date && filters.start_date !== "null" && { start_date: filters.start_date }),
        ...(filters.end_date && filters.end_date !== "null" && { end_date: filters.end_date }),
    };

    const {
        isLoading,
        data: warrantyRegistrations,
        isFetching,
        refetch,
    } = useGetAdminWarrantyRegistrationsQuery(queryParams);
    const { data: productsData } = useGetProductsDropdownQuery();

    useEffect(() => {
        props.handlePaginationState(defaultPaginate);
    }, []);

    const handlePagination = (setting) => {
        props.handlePaginationState(setting);
    };

    const handleFilterChange = (filterName, value) => {
        props.updateFilters({
            [filterName]: value,
        });
        props.handlePaginationState({
            ...pagination,
            pageNo: 0,
        });
    };

    const handleClearFilters = () => {
        props.resetFilters();
        props.handlePaginationState({
            ...pagination,
            keywords: "",
            pageNo: 0,
        });
    };

    const handleViewDetails = (warranty) => {
        console.log("handleViewDetails called with warranty:", warranty);
        setSelectedWarrantyForDetails(warranty);
        props.setShowWarrantyDetails(true);
    };

    const handleDelete = (warranty) => {
        console.log("Delete warranty:", warranty);
        // TODO: Implement delete API call
        // For now, just refetch to show updated data
        refetch();
    };

    const handleCloseDetails = () => {
        setSelectedWarrantyForDetails(null);
        props.setShowWarrantyDetails(false);
    };

    const getActiveFiltersCount = () => {
        let count = 0;
        if (filters.product_id) count++;
        if (filters.start_date) count++;
        if (filters.end_date) count++;
        return count;
    };

    return (
        <Stack spacing={2}>
            {/* Header */}
            <Grid container justifyContent="space-between" alignItems="center">
                <Grid item xs={6} sx={{ alignSelf: "center" }}>
                    <Typography variant="h6">
                        Warranty Registrations
                        <span style={{ color: "gray", fontSize: "10px", verticalAlign: "super", marginLeft: 8 }}>
                            ({warrantyRegistrations?.count || 0})
                        </span>
                    </Typography>
                </Grid>
                <Grid
                    item
                    xs={6}
                    sx={{ alignSelf: "center" }}
                    display={"flex"}
                    flexDirection={"row"}
                    justifyContent={"flex-end"}
                >
                    <SearchRecords handlePagination={handlePagination} pagination={pagination} />
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ ml: 2 }}>
                        <Tooltip title="Refresh">
                            <IconButton onClick={() => refetch()} size="small">
                                <RefreshIcon />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Export">
                            <IconButton size="small">
                                <ExportIcon />
                            </IconButton>
                        </Tooltip>
                        <Button
                            variant="outlined"
                            startIcon={<FilterListIcon />}
                            onClick={() => setShowFilters(!showFilters)}
                            size="small"
                        >
                            Filters
                            {getActiveFiltersCount() > 0 && (
                                <Chip
                                    label={getActiveFiltersCount()}
                                    size="small"
                                    sx={{
                                        ml: 1,
                                        height: 20,
                                        fontSize: "0.7rem",
                                        backgroundColor: "#1976d2",
                                        color: "white",
                                    }}
                                />
                            )}
                        </Button>
                    </Stack>
                </Grid>
            </Grid>

            {/* Filters */}
            {showFilters && (
                <Paper sx={{ p: 3, mb: 2 }}>
                    <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                        Filters
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6} md={4}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Product</InputLabel>
                                <Select
                                    value={filters.product_id || ""}
                                    onChange={(e) => handleFilterChange("product_id", e.target.value || null)}
                                    label="Product"
                                >
                                    <MenuItem value="">All Products</MenuItem>
                                    {productsData?.map((product) => (
                                        <MenuItem key={product.value} value={product.value}>
                                            {product.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <TextField
                                fullWidth
                                size="small"
                                type="date"
                                label="Start Date"
                                value={filters.start_date || ""}
                                onChange={(e) => handleFilterChange("start_date", e.target.value || null)}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ height: "100%" }}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    type="date"
                                    label="End Date"
                                    value={filters.end_date || ""}
                                    onChange={(e) => handleFilterChange("end_date", e.target.value || null)}
                                    InputLabelProps={{ shrink: true }}
                                />
                                <Button
                                    variant="outlined"
                                    startIcon={<ClearIcon />}
                                    onClick={handleClearFilters}
                                    size="small"
                                    disabled={getActiveFiltersCount() === 0}
                                >
                                    Clear
                                </Button>
                            </Stack>
                        </Grid>
                    </Grid>
                </Paper>
            )}

            {/* Warranty Table */}
            <Grid container>
                <Grid item xs={12}>
                    <WarrantyTableComponent
                        loading={isLoading}
                        fetching={isFetching}
                        count={warrantyRegistrations?.count}
                        data={warrantyRegistrations?.results || []}
                        onViewDetails={handleViewDetails}
                        onDelete={handleDelete}
                        pagination={pagination}
                        handlePagination={handlePagination}
                    />
                </Grid>
            </Grid>

            {/* Warranty Details Dialog */}
            <WarrantyDetailsDialog
                open={showWarrantyDetails}
                onClose={handleCloseDetails}
                warrantyId={selectedWarrantyForDetails?.id}
            />
        </Stack>
    );
};

export default WarrantyContainer;
